import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { usePermissions } from "@/hooks/usePermissions";
import axios from "axios";
import { toast } from "sonner";
import {
  ChatCircle,
  PaperPlaneTilt,
  Plus,
  User,
  Circle,
  MagnifyingGlass,
  ArrowLeft
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const ROLE_LABELS = { admin: "Admin", commercial: "Commercial", preparateur: "Preparateur", auditeur: "Auditeur", comptabilite: "Compta" };

export default function Chat() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [users, setUsers] = useState([]);
  const [showNewConvo, setShowNewConvo] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");

  const loadConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/conversations`);
      setConversations(res.data);
    } catch {}
  }, []);

  const loadMessages = useCallback(async (convoId) => {
    try {
      const res = await axios.get(`${API}/chat/conversations/${convoId}/messages`);
      setMessages(res.data);
    } catch {}
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/users`);
      setUsers(res.data);
    } catch {}
  }, []);

  useEffect(() => { loadConversations(); loadUsers(); }, [loadConversations, loadUsers]);

  useEffect(() => {
    if (!activeConvo) return;
    loadMessages(activeConvo.conversation_id);
    const interval = setInterval(() => {
      loadMessages(activeConvo.conversation_id);
      loadConversations();
    }, 5000);
    return () => clearInterval(interval);
  }, [activeConvo, loadMessages, loadConversations]);

  const startConversation = async (targetUser) => {
    try {
      const res = await axios.post(`${API}/chat/conversations`, {
        participant_ids: [user.user_id, targetUser.user_id],
        title: ""
      });
      setShowNewConvo(false);
      setActiveConvo(res.data);
      await loadConversations();
      loadMessages(res.data.conversation_id);
    } catch { toast.error("Erreur creation conversation"); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConvo) return;
    setSending(true);
    try {
      await axios.post(`${API}/chat/conversations/${activeConvo.conversation_id}/messages`, {
        content: newMessage.trim()
      });
      setNewMessage("");
      loadMessages(activeConvo.conversation_id);
      loadConversations();
    } catch { toast.error("Erreur envoi"); }
    setSending(false);
  };

  const getConvoName = (convo) => {
    if (convo.title) return convo.title;
    const names = convo.participant_names || {};
    const otherNames = Object.entries(names).filter(([id]) => id !== user?.user_id).map(([, n]) => n);
    return otherNames.join(", ") || "Conversation";
  };

  const getInitials = (name) => name ? name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) : "?";

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="chat-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Chat Interne</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Communiquez avec votre equipe</p>
        </div>
        <Button onClick={() => setShowNewConvo(true)} className="btn-primary" data-testid="new-convo-btn">
          <Plus size={16} className="mr-1.5" /> Nouvelle conversation
        </Button>
      </div>

      <div className="flex-1 flex gap-4 min-h-0 overflow-hidden">
        {/* Conversation list */}
        <div className={`card flex flex-col ${activeConvo ? 'hidden md:flex' : 'flex'} w-full md:w-80 shrink-0`}>
          <div className="p-3 border-b border-slate-200 dark:border-slate-700">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-sm" data-testid="chat-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.filter(c => getConvoName(c).toLowerCase().includes(search.toLowerCase())).map(convo => (
              <button key={convo.conversation_id} onClick={() => setActiveConvo(convo)}
                className={`w-full px-3 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800 border-b border-slate-100 dark:border-slate-800 text-left transition-colors ${activeConvo?.conversation_id === convo.conversation_id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                data-testid={`convo-${convo.conversation_id}`}>
                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">{getInitials(getConvoName(convo))}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{getConvoName(convo)}</span>
                    {convo.unread > 0 && <span className="bg-blue-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{convo.unread}</span>}
                  </div>
                  {convo.last_message && <p className="text-xs text-slate-500 truncate mt-0.5">{convo.last_message.content}</p>}
                </div>
              </button>
            ))}
            {conversations.length === 0 && (
              <div className="text-center py-8 text-sm text-slate-400">
                <ChatCircle size={32} className="mx-auto mb-2 opacity-50" />
                Aucune conversation
              </div>
            )}
          </div>
        </div>

        {/* Message area */}
        <div className={`card flex-1 flex flex-col min-w-0 ${!activeConvo ? 'hidden md:flex' : 'flex'}`}>
          {activeConvo ? (
            <>
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
                <button onClick={() => setActiveConvo(null)} className="md:hidden"><ArrowLeft size={18} /></button>
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">{getInitials(getConvoName(activeConvo))}</AvatarFallback>
                </Avatar>
                <span className="font-medium text-sm text-slate-900 dark:text-slate-100">{getConvoName(activeConvo)}</span>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {messages.map(msg => {
                  const isMe = msg.sender_id === user?.user_id;
                  return (
                    <div key={msg.message_id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] rounded-2xl px-3.5 py-2 ${isMe ? 'bg-blue-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100'}`}>
                        {!isMe && <p className="text-[10px] font-medium opacity-70 mb-0.5">{msg.sender_name}</p>}
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-[10px] mt-1 ${isMe ? 'text-blue-200' : 'text-slate-400'}`}>
                          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString("fr-BE", { hour: "2-digit", minute: "2-digit" }) : ""}
                        </p>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && <p className="text-center text-sm text-slate-400 py-8">Debut de la conversation</p>}
              </div>
              <form onSubmit={sendMessage} className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex gap-2">
                <Input value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Ecrivez un message..."
                  className="flex-1" disabled={sending} data-testid="chat-input" autoComplete="off" />
                <Button type="submit" disabled={sending || !newMessage.trim()} className="btn-primary" data-testid="chat-send-btn">
                  <PaperPlaneTilt size={16} />
                </Button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <ChatCircle size={48} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm">Selectionnez une conversation</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New conversation dialog */}
      <Dialog open={showNewConvo} onOpenChange={setShowNewConvo}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle conversation</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {users.map(u => (
              <button key={u.user_id} onClick={() => startConversation(u)}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
                data-testid={`start-convo-${u.user_id}`}>
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-slate-200 dark:bg-slate-700 text-sm">{getInitials(u.name)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{u.name}</p>
                  <p className="text-xs text-slate-500">{ROLE_LABELS[u.role] || u.role}</p>
                </div>
              </button>
            ))}
            {users.length === 0 && <p className="text-center text-sm text-slate-400 py-4">Aucun autre utilisateur</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
